/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

export class StorageWrapper implements Storage {

    private readonly storage: Storage | Record<string, string>;

    readonly isSupported: boolean = false;

    constructor(storage: Storage) {
        if (StorageWrapper.isSupported(storage)) {
            this.isSupported = true;
            this.storage = storage;
        } else {
            this.storage = {};
        }
    }

    get length(): number {
        if (this.isSupported) {
            return (this.storage as Storage).length;
        }

        return Object.keys(this.storage).length;
    }

    clear(): void {
        if (this.isSupported) {
            (this.storage as Storage).clear();
        } else {
            (this.storage as Record<string, string>) = {};
        }
    }

    getItem(key: string): string | null {
        if (this.isSupported) {
            const item = (this.storage as Storage).getItem(key);

            return item === null || item === 'null' ? null : item;
        }

        return this.storage[key];
    }

    setItem(key: string, value: string | null): void {
        if (this.isSupported) {
            // can be error QuotaExceededError
            (this.storage as Storage).setItem(key, value);
        } else {
            this.storage[key] = value;
        }
    }

    key(index: number): string | null {
        if (this.isSupported) {
            return (this.storage as Storage).key(index);
        }

        return Object.values(this.storage)[index];
    }

    removeItem(key: string): void {
        if (this.isSupported) {
            (this.storage as Storage).removeItem(key);
        } else {
            delete this.storage[key];
        }
    }

    removeMultipleItems(includeKeyPart: string, excludeKeys: string[] = []): void {
        if (this.isSupported) {
            for (let i = 0; i < (this.storage as Storage).length; i += 1) {
                const key = (this.storage as Storage).key(i);

                if (
                    key
                    && key.indexOf(includeKeyPart) === 0
                    && excludeKeys.every((excludeKey: string): boolean => key.indexOf(excludeKey) !== 0)
                ) {
                    (this.storage as Storage).removeItem(key);
                    i -= 1;
                }
            }
        } else {
            const storageKeys: string[] = Object.keys(this.storage);

            for (let i = 0; i < storageKeys.length; i += 1) {
                const key = storageKeys[i];

                if (
                    key.indexOf(includeKeyPart) === 0
                    && excludeKeys.every((excludeKey: string): boolean => key.indexOf(excludeKey) !== 0)
                ) {
                    delete this.storage[key];
                }
            }
        }
    }

    private static isSupported(storage: Storage): boolean {
        try {
            const testKey = 'test__key';

            storage.setItem(testKey, testKey);
            storage.removeItem(testKey);

            return true;
        } catch (err) {
            if ((err as Error).name === 'QuotaExceededError') {
                storage.clear();
                return true;
            }

            return false;
        }
    }

}
